/**
 * The new layout's shared state (AppShellV2): the person's shell preferences, Lykke's panel (open, width, which
 * mode), the right drawer, and the slots pages put their agent work into.
 *
 * Page modes: a page whose agent does specialised work (the Deck studio builds decks, Order a campaign writes the
 * order) does not open a second chat. It puts that conversation into Lykke's panel as a mode of hers:
 *
 *   <AgentPanelSlot id="deck" label="This deck">{thread}<AgentComposer … /></AgentPanelSlot>
 *
 * In the new layout the children render inside the panel (a portal, so they keep the page's state and context);
 * in the classic layout `AgentPanelSlot` renders `fallback` in place instead. The panel switches to a page's mode
 * when the page opens, and the person can always go back to the whole conversation ("Lykke").
 *
 * The right drawer: any page opens config, a profile, details or a second conversation on the right:
 *
 *   const drawer = useRightDrawer(); drawer.open({ id: 'details', title: 'Details', content: <Details /> })
 *   <RightDrawerPortal open={o} onClose={…} title="Filters">…</RightDrawerPortal>   (live content)
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { readShellPrefs, writeShellPrefs, clampAgentWidth, type ShellPrefs } from './layoutPref';

export interface PanelMode { id: string; label: string; /** A short line under the name ("Writing your order"). */ line?: string }
export interface DrawerSpec { id: string; title: string; content?: ReactNode; width?: number; onClose?: () => void }

interface ShellValue {
  prefs: ShellPrefs;
  setPrefs: (p: Partial<ShellPrefs>) => void;
  agentOpen: boolean;
  setAgentOpen: (open: boolean) => void;
  /** Open the panel and put the cursor in its box (the campaign page's "Ask Lykke", a shortcut). */
  focusAgent: () => void;
  registerFocus: (fn: (() => void) | null) => void;
  /** 'lykke' (the whole conversation), a page mode id, or a staff engine ('guide', 'agents'). */
  view: string;
  setView: (v: string) => void;
  modes: PanelMode[];
  registerMode: (m: PanelMode) => () => void;
  slot: (id: string) => HTMLElement | null;
  setSlot: (id: string, el: HTMLElement | null) => void;
  drawer: DrawerSpec | null;
  openDrawer: (d: DrawerSpec) => void;
  closeDrawer: () => void;
  drawerHost: HTMLElement | null;
  setDrawerHost: (el: HTMLElement | null) => void;
  isPhone: boolean;
}

const Ctx = createContext<ShellValue | null>(null);

/** Inside the new layout? (False in the classic layout and in the back office frame.) */
// eslint-disable-next-line react-refresh/only-export-components -- the hooks belong with their provider
export function useInShell(): boolean { return useContext(Ctx) !== null; }
// eslint-disable-next-line react-refresh/only-export-components -- the hooks belong with their provider
export function useShell(): ShellValue | null { return useContext(Ctx); }

const PHONE = '(max-width: 1023px)';
function usePhone(): boolean {
  const [phone, setPhone] = useState(() => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(PHONE).matches : false));
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(PHONE);
    const on = () => setPhone(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return phone;
}

export function ShellProvider({ email, children }: { email?: string | null; children: ReactNode }) {
  const [prefs, setPrefsState] = useState<ShellPrefs>(() => readShellPrefs(email));
  useEffect(() => { setPrefsState(readShellPrefs(email)); }, [email]);
  const isPhone = usePhone();
  // On a phone the panel is a bottom sheet: closed until asked for, whatever the desktop choice is.
  const [phoneOpen, setPhoneOpen] = useState(false);
  const setPrefs = useCallback((p: Partial<ShellPrefs>) => {
    setPrefsState((cur) => { const next = { ...cur, ...p, agentWidth: clampAgentWidth(p.agentWidth ?? cur.agentWidth) }; writeShellPrefs(email, next); return next; });
  }, [email]);
  const agentOpen = isPhone ? phoneOpen : prefs.agentOpen;
  const setAgentOpen = useCallback((open: boolean) => { if (isPhone) setPhoneOpen(open); else setPrefs({ agentOpen: open }); }, [isPhone, setPrefs]);

  const focusFn = useRef<(() => void) | null>(null);
  const registerFocus = useCallback((fn: (() => void) | null) => { focusFn.current = fn; }, []);
  const [view, setView] = useState('lykke');
  const focusAgent = useCallback(() => { setAgentOpen(true); setTimeout(() => focusFn.current?.(), 60); }, [setAgentOpen]);

  const [modes, setModes] = useState<PanelMode[]>([]);
  const registerMode = useCallback((m: PanelMode) => {
    setModes((cur) => [...cur.filter((x) => x.id !== m.id), m]);
    // A page that opens with its own agent work shows it at once.
    setView(m.id);
    return () => {
      setModes((cur) => cur.filter((x) => x.id !== m.id));
      setView((v) => (v === m.id ? 'lykke' : v));
    };
  }, []);
  const [slots, setSlots] = useState<Record<string, HTMLElement | null>>({});
  const setSlot = useCallback((id: string, el: HTMLElement | null) => setSlots((cur) => (cur[id] === el ? cur : { ...cur, [id]: el })), []);
  const slot = useCallback((id: string) => slots[id] || null, [slots]);

  const [drawer, setDrawer] = useState<DrawerSpec | null>(null);
  const drawerRef = useRef<DrawerSpec | null>(null); drawerRef.current = drawer;
  const openDrawer = useCallback((d: DrawerSpec) => setDrawer(d), []);
  const closeDrawer = useCallback(() => { const d = drawerRef.current; setDrawer(null); d?.onClose?.(); }, []);
  const [drawerHost, setDrawerHost] = useState<HTMLElement | null>(null);

  const value = useMemo<ShellValue>(() => ({
    prefs, setPrefs, agentOpen, setAgentOpen, focusAgent, registerFocus, view, setView, modes, registerMode, slot, setSlot,
    drawer, openDrawer, closeDrawer, drawerHost, setDrawerHost, isPhone,
  }), [prefs, setPrefs, agentOpen, setAgentOpen, focusAgent, registerFocus, view, modes, registerMode, slot, setSlot, drawer, openDrawer, closeDrawer, drawerHost, isPhone]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * A page's agent work as a mode of Lykke's panel (new layout), or `fallback` in place (classic layout).
 * The children stay mounted while the person looks at another mode or closes the panel.
 */
export function AgentPanelSlot({ id, label, line, children, fallback }: { id: string; label: string; line?: string; children: ReactNode; fallback?: ReactNode }) {
  const shell = useShell();
  const register = shell?.registerMode;
  useEffect(() => (register ? register({ id, label, line }) : undefined), [register, id, label, line]);
  if (!shell) return <>{fallback ?? null}</>;
  const el = shell.slot(id);
  return el ? createPortal(children, el) : null;
}

/** Open, replace or close the right drawer. Outside the new layout `available` is false (pages keep their own UI). */
// eslint-disable-next-line react-refresh/only-export-components -- the hooks belong with their provider
export function useRightDrawer() {
  const shell = useShell();
  return useMemo(() => ({
    available: !!shell,
    current: shell?.drawer || null,
    open: (d: DrawerSpec) => shell?.openDrawer(d),
    close: () => shell?.closeDrawer(),
  }), [shell]);
}

/** Live content in the right drawer (it re-renders with the page). Renders nothing outside the new layout. */
export function RightDrawerPortal({ id, open, onClose, title, width, children }: { id: string; open: boolean; onClose: () => void; title: string; width?: number; children: ReactNode }) {
  const shell = useShell();
  const openDrawer = shell?.openDrawer; const closeDrawer = shell?.closeDrawer;
  const current = shell?.drawer?.id;
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  useEffect(() => {
    if (!openDrawer) return;
    if (open) openDrawer({ id, title, width, onClose: () => onCloseRef.current() });
    else if (current === id) closeDrawer?.();
  }, [open, id, title, width, openDrawer]);
  if (!shell || !open || shell.drawer?.id !== id || !shell.drawerHost) return null;
  return createPortal(children, shell.drawerHost);
}
